import React from 'react';
import { X, MapPin, Building2, Network, Shield } from 'lucide-react';

const ShodanResultModal = ({ isOpen, onClose, result }) => {
  if (!isOpen || !result) return null;

  const data = result.data || {};
  const {
    ip,
    port,
    protocol,
    service,
    version,
    banner,
    org,
    isp,
    country,
    city,
    vulns = []
  } = data;

  const asn = data.asn || 'N/A';
  const firstSeen = result.created_at ? new Date(result.created_at).toLocaleString() : 'N/A';

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="cyber-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">
              Scan Result Details
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Type: <span className="text-purple-600">port</span> | Value: <span className="text-shinobi-orange">{ip}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Top Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Network className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-500 uppercase">IP Address</span>
              </div>
              <div className="text-lg font-mono text-shinobi-orange">{ip}</div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Building2 className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-500 uppercase">Organization</span>
              </div>
              <div className="text-lg text-slate-800 truncate">{org || 'N/A'}</div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <MapPin className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-500 uppercase">Location</span>
              </div>
              <div className="text-lg text-slate-800">{city && country ? `${city}, ${country}` : country || city || 'N/A'}</div>
            </div>
          </div>

          {/* Vulnerabilities */}
          {vulns && vulns.length > 0 ? (
            <div className="bg-red-900/10 border border-red-300 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-danger-red" />
                  <h3 className="text-lg font-semibold text-danger-red">
                    Vulnerabilities ({vulns.length})
                  </h3>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                {vulns.map((vuln, index) => (
                  <div
                    key={index}
                    className="px-3 py-2 bg-red-900/30 border border-red-300 text-danger-red rounded text-xs font-mono text-center hover:bg-red-900/50 transition-colors cursor-pointer"
                    title={vuln}
                  >
                    {vuln}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-green-900/10 border border-green-300 rounded-lg p-5">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-green-500" />
                <h3 className="text-lg font-semibold text-green-500">
                  No Known Vulnerabilities
                </h3>
              </div>
            </div>
          )}

          {/* Additional Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 uppercase mb-2">ASN</div>
              <div className="text-sm text-slate-800 font-mono">{asn}</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 uppercase mb-2">ISP</div>
              <div className="text-sm text-slate-800">{isp || 'N/A'}</div>
            </div>
          </div>

          {/* Port and Service Info */}
          {port && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500 uppercase mb-1">Port</div>
                  <div className="text-shinobi-orange font-mono">{port}/{protocol || 'tcp'}</div>
                </div>
                {service && service !== 'unknown' && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase mb-1">Service</div>
                    <div className="text-slate-800">{service}</div>
                  </div>
                )}
                {version && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase mb-1">Version</div>
                    <div className="text-slate-800">{version}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Banner */}
          {banner && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 uppercase mb-2">Service Banner</div>
              <pre className="text-xs text-slate-500 font-mono bg-cyber-100 p-3 rounded overflow-x-auto whitespace-pre-wrap break-all max-h-48">
                {banner}
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-slate-200">
          <div className="text-sm text-gray-500">
            First seen: {firstSeen}
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-shinobi-orange hover:bg-shinobi-orange/80 text-cyber-50 rounded transition-colors font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShodanResultModal;
